import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, InputGroup, Modal, Row, Table } from 'react-bootstrap';
import { FaCheck, FaEdit, FaEnvelope, FaEye, FaEyeSlash, FaGlobe, FaLock, FaPhone, FaPlus, FaShoppingCart, FaTrash, FaUser } from 'react-icons/fa';
import { useTable } from 'react-table';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_URL;


function BusinessAuditorCreate() {
  // Table state
  const [auditors, setAuditors] = useState([]);
//   const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAuditor, setSelectedAuditor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Create auditor modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  // Add these state variables after your existing useState declarations
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);

  // Add these state variables at the beginning of your component
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const CompanyCode = userData?.CompanyCode;
  
  // Form data state
  const [formData, setFormData] = useState({
    accountType: 'business',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
    password: '',
    confirmPassword: '',
    plan: userData?.plan || 'trial',
    country: 'lk',
    CompanyCode: CompanyCode,
    role: 'Company auditor'
  });
  
  // Form validation errors
  const [errors, setErrors] = useState({});


  const initialFormData = {
    accountType: 'business',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
    password: '',
    confirmPassword: '',
    plan: userData?.plan || 'trial',
    country: 'lk',
    CompanyCode: CompanyCode,
    role: 'Company auditor'
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setShowPasswordFields(false);
    setShowEditPassword(false);
    setShowEditConfirmPassword(false);
    setErrors({});
  };
  

  // Add these close handlers
  const handleEditModalClose = () => {
    setShowEditModal(false);
    resetForm();
  };

  const handleCreateModalClose = () => {
    setShowCreateModal(false);
    resetForm();
  };

  // Add this function after your state declarations
  const checkAuditorLimit = async () => {
    try {
      const response = await fetch(`${BASE_URL}/check-business-auditor-limit?CompanyCode=${CompanyCode}&plan=${userData.plan}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to check auditor limit');
      }

      return data.currentCount < data.limit;
    } catch (error) {
      console.error("Error checking auditor limit:", error);
      toast.error('Failed to check auditor limit');
      return false;
    }
  };

  // Fetch auditors data
  const [isLoading, setIsLoading] = useState(false);

  const fetchAuditors = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/get-business-auditors?CompanyCode=${CompanyCode}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      setAuditors(data);
      setError('');
    } catch (err) {
      console.error("Failed to fetch auditors:", err);
      setError('Failed to load auditors. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load auditors on component mount
  useEffect(() => {
    fetchAuditors();
  }, [CompanyCode]);



  // Table data
  const columns = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'firstName', // accessor is the key to the data
        Cell: ({ row }) => `${row.original.firstName} ${row.original.lastName}`, // Combine first and last names
      },
      {
        Header: 'Email',
        accessor: 'emailAddress',
      },
      {
        Header: 'Phone Number',
        accessor: 'phoneNumber',
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => (
          <div className="d-flex gap-2 justify-content-center">
            <Button 
              variant="outline-warning" 
              size="sm" 
              onClick={async () => {
                setSelectedAuditor(row.original);
                setFormData({
                  ...initialFormData,
                  firstName: row.original.firstName,
                  lastName: row.original.lastName,
                  phoneNumber: row.original.phoneNumber,
                  country: row.original.country || 'lk',
                  password: '',
                  confirmPassword: ''
                });

                setShowPasswordFields(false);
                setShowEditPassword(false);
                setShowEditConfirmPassword(false);
                setShowEditModal(true);
              }}
              className="d-flex align-items-center"
            >
              <FaEdit className="me-1" /> Edit
            </Button>
            <Button 
              variant="outline-danger" 
              size="sm" 
              onClick={() => {
                setSelectedAuditor(row.original);
                setShowDeleteModal(true);
              }}
              className="d-flex align-items-center"
            >
              <FaTrash className="me-1" /> Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  // Create a table instance using react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data: auditors,
  });







  // Handle delete auditor
  const handleDeleteAuditor = async () => {
    if (!selectedAuditor) return;
    
    try {
      const response = await fetch(`${BASE_URL}/delete-business-auditor/${selectedAuditor.id}`, {
        method: 'DELETE',
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete auditor');
      }
      
      toast.success('Auditor deleted successfully!');
      setShowDeleteModal(false);
      fetchAuditors();
      
    } catch (err) {
      console.error("Error deleting auditor:", err);
      toast.error(err.message || 'Failed to delete auditor. Please try again.');
    }
  };
  
  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Handle input changes for create form
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.phoneNumber) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.emailAddress) newErrors.emailAddress = 'Email is required';

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.emailAddress && !emailRegex.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Invalid email format';
    }

    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Strong password validation
    const hasUppercase = /[A-Z]/.test(formData.password);
    const hasLowercase = /[a-z]/.test(formData.password);
    const hasNumber = /[0-9]/.test(formData.password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);

    if (formData.password && !(hasUppercase && hasLowercase && hasNumber && hasSpecial)) {
      newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.country) newErrors.country = 'Country is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



const handleCreateSubmit = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) {
      return;
    }
  
    setCreateLoading(true);
  
    try {
      const response = await fetch(`${BASE_URL}/register-business-auditor`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData),
      });
  
      const data = await response.json();
  
      if (response.ok) {
        toast.success('Auditor created successfully! Verify your email to activate your account.');
        setShowCreateModal(false);
        resetForm();
        fetchAuditors();
      } else {
        if (data.message === "Auditor registration limit reached for this plan.") {
          setShowCreateModal(false);
          resetForm();
          setShowPurchaseModal(true);
        } else {
          throw new Error(data.message || "Registration failed");
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.message || "Failed to create auditor. Please try again.");
    } finally {
      setCreateLoading(false);
    }
  };
  
  

// Add this function after your other handler functions
const handleEditSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateEditForm()) {
    return;
  }

  setEditLoading(true);

  try {
    const updateData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phoneNumber: formData.phoneNumber,
      country: formData.country
    };

    if (formData.password) {
      updateData.password = formData.password;
    }

    const response = await fetch(`${BASE_URL}/update-business-auditor/${selectedAuditor.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    const data = await response.json();

    if (response.ok) {
      toast.success('Auditor updated successfully!');
      setShowEditModal(false);
      resetForm();
      fetchAuditors();
    } else {
      throw new Error(data.message || 'Update failed');
    }

  } catch (error) {
    console.error("Update error:", error);
    toast.error(error.message || 'Failed to update auditor. Please try again.');
  } finally {
    setEditLoading(false);
  }
};

// Add validation function for edit form
const validateEditForm = () => {
  const newErrors = {};

  if (!formData.firstName) newErrors.firstName = 'First name is required';
  if (!formData.lastName) newErrors.lastName = 'Last name is required';
  if (!formData.phoneNumber) newErrors.phoneNumber = 'Phone number is required';

  if (formData.password) {
    if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    const hasUppercase = /[A-Z]/.test(formData.password);
    const hasLowercase = /[a-z]/.test(formData.password);
    const hasNumber = /[0-9]/.test(formData.password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);

    if (!(hasUppercase && hasLowercase && hasNumber && hasSpecial)) {
      newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};


// Handle Buy Now action
    const handleBuyNow = () => {
        // Close the purchase modal
        setShowPurchaseModal(false);
        
        // Add your buy now implementation here
        // This could be navigating to a payment page or opening another modal
        
        // Example implementation:
        // window.location.href = "/subscription-plans";
        // or
        // navigate("/payment", { state: { plan: "premium" } });
      };

  // Filter auditors based on search term
  const filteredAuditors = auditors.filter(auditor => {
    const fullName = `${auditor.firstName} ${auditor.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) || 
           auditor.emailAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
           auditor.phoneNumber.includes(searchTerm);
  });
  
  // Countries list for dropdown
  const countries = [
    { value: 'lk', label: 'Sri Lanka' },
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'ca', label: 'Canada' },
    { value: 'au', label: 'Australia' },
    { value: 'in', label: 'India' },
  ];

  return (
    <Container fluid className="py-4">
      {/* Notification alert */}
      <ToastContainer />
      
      <Container fluid className="py-3">
      <Row className="justify-content-center">
        <Col xs={12} lg={10} xl={9}>
        <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white">
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-0">Auditors</h4>
            </Col>
            <Col md="auto">
              <Button 
                variant="light" 
                className="d-flex align-items-center"
                onClick={async () => {
                  const canAddAuditor = await checkAuditorLimit();
                  if (canAddAuditor) {
                    setShowCreateModal(true);
                  } else {
                    setShowPurchaseModal(true);
                  }
                }}
              >
                <FaPlus className="me-2" /> Add Auditor
              </Button>
            </Col>
          </Row>
        </Card.Header>
        
        <Card.Body>
  {/* Error message */}
  {error && <Alert variant="danger">{error}</Alert>}
  
  {/* Enhanced Loading indicator */}
  {isLoading && (
    <div className="text-center py-5">
      <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="text-primary fw-bold mb-1">Loading Auditors</p>
      <p className="text-muted">Please wait while we fetch your auditor data...</p>
    </div>
  )}
  
  {/* Table content - only show when not loading */}
  {!isLoading && (
    <>
      {auditors.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted mb-0">No auditors found. Add your first auditor.</p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="d-md-none">
            {auditors.map((auditor) => (
              <Card key={auditor.id} className="mb-3 auditor-card border shadow-sm">
                <Card.Body className="p-3">
                  <h5 className="mb-2">{auditor.firstName} {auditor.lastName}</h5>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">
                      <FaEnvelope className="me-2" />{auditor.emailAddress}
                    </small>
                    <small className="text-muted d-block">
                      <FaPhone className="me-2" />{auditor.phoneNumber}
                    </small>
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button 
                      variant="outline-warning" 
                      size="sm" 
                      onClick={() => {
                        setSelectedAuditor(auditor);
                        setFormData({
                          ...initialFormData,
                          firstName: auditor.firstName,
                          lastName: auditor.lastName,
                          phoneNumber: auditor.phoneNumber,
                          country: auditor.country || 'lk',
                          password: '',
                          confirmPassword: ''
                        });
                        setShowPasswordFields(false);
                        setShowEditPassword(false);
                        setShowEditConfirmPassword(false);
                        setShowEditModal(true);
                      }}
                      className="d-flex align-items-center"
                    >
                      <FaEdit className="me-1" /> Edit
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={() => {
                        setSelectedAuditor(auditor);
                        setShowDeleteModal(true);
                      }}
                      className="d-flex align-items-center"
                    >
                      <FaTrash className="me-1" /> Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
          
          {/* Desktop Table View */}
          <div className="table-responsive d-none d-md-block">
            <Table hover striped bordered {...getTableProps()} className="align-middle">
              <thead className="bg-light">
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => {
                        return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </>
  )}
</Card.Body>
        
        {/* <Card.Footer className="bg-light">
          <small className="text-muted">
            Showing {filteredAuditors.length} of {auditors.length} auditors
          </small>
        </Card.Footer> */}
      </Card>
        </Col>
        </Row>
        </Container>

      
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the auditor <strong>{selectedAuditor?.firstName} {selectedAuditor?.lastName}</strong>?
          <p className="text-danger mt-2 mb-0">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteAuditor}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Create Auditor Modal */}
      <Modal 
        show={showCreateModal} 
        onHide={handleCreateModalClose} 
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>Add Individual Auditor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
       
          <Form onSubmit={handleCreateSubmit} noValidate>
          {error && <Alert variant="danger">{error}</Alert>}
            {/* Personal Information Section */}
            <h5 className="border-bottom pb-2 mb-3">Personal Information</h5>

            {/* First Name and Last Name */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>First Name</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaUser /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      isInvalid={!!errors.firstName}
                      placeholder="Enter first name"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.firstName}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Last Name</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaUser /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      isInvalid={!!errors.lastName}
                      placeholder="Enter last name"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.lastName}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* Contact Information Section */}
            <h5 className="border-bottom pb-2 mb-3 mt-4">Contact Information</h5>

            {/* Phone Number and Email */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Phone Number</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaPhone /></InputGroup.Text>
                    <Form.Control
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      isInvalid={!!errors.phoneNumber}
                      placeholder="Enter phone number"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phoneNumber}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email Address</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="emailAddress"
                      value={formData.emailAddress}
                      onChange={handleChange}
                      isInvalid={!!errors.emailAddress}
                      placeholder="Enter email address"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.emailAddress}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* Country */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Country</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaGlobe /></InputGroup.Text>
                    <Form.Select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      isInvalid={!!errors.country}
                    >
                      {countries.map((country) => (
                        <option key={country.value} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.country}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* Security Section */}
            <h5 className="border-bottom pb-2 mb-3 mt-4">Security</h5>

            {/* Password and Confirm Password */}
            <Row className="mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Password</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaLock /></InputGroup.Text>
                    <Form.Control
                      type={showPassword ? "text" : "password"} 
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      isInvalid={!!errors.password}
                      placeholder="Create password"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {errors.password}
                    </Form.Control.Feedback>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Confirm Password</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaLock /></InputGroup.Text>
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"} 
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      isInvalid={!!errors.confirmPassword}
                      placeholder="Confirm password"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={toggleConfirmPasswordVisibility}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {errors.confirmPassword}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* Hidden fields */}
            <input type="hidden" name="accountType" value={formData.accountType} />
            <input type="hidden" name="plan" value={formData.plan} />
            <input type="hidden" name="CompanyCode" value={formData.CompanyCode} />
            <input type="hidden" name="role" value={formData.role} />
          </Form>
        </Modal.Body>
        <Modal.Footer>
        <Button variant="secondary" onClick={handleCreateModalClose}>
                 Cancel
            </Button>

          <Button 
            variant="primary" 
            onClick={handleCreateSubmit}
            disabled={createLoading}
          >
            {createLoading ? "Creating..." : "Create Auditor"}
          </Button>
        </Modal.Footer>
      </Modal>

        {/* Purchase Modal for Branch Limit */}
<Modal
  show={showPurchaseModal}
  onHide={() => setShowPurchaseModal(false)}
  centered
  backdrop="static"
>
  <Modal.Header closeButton className="bg-primary text-white">
    <Modal.Title>Upgrade Your Plan</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <div className="text-center mb-4">
      <FaShoppingCart className="text-primary" size={50} />
      <h4 className="mt-3">Auditor Limit Reached</h4>
      <p className="text-muted">
        Your current {userData.plan} plan has reached its maximum auditor limit.
      </p>
    </div>
    
    <div className="bg-light p-4 rounded">
      <h5 className="mb-3">Premium Plan Benefits:</h5>
      <ul className="list-unstyled">
        <li className="mb-2">
          <FaCheck className="text-success me-2" /> Up to 10 auditors per branch
        </li>
        <li className="mb-2">
          <FaCheck className="text-success me-2" /> Advanced reporting features
        </li>
        <li className="mb-2">
          <FaCheck className="text-success me-2" /> Priority customer support
        </li>
        <li className="mb-2">
          <FaCheck className="text-success me-2" /> Custom branding options
        </li>
      </ul>
    </div>
  </Modal.Body>
  <Modal.Footer>
    <Button 
      variant="secondary" 
      onClick={() => setShowPurchaseModal(false)}
    >
      Maybe Later
    </Button>
    <Button 
      variant="primary" 
      onClick={handleBuyNow}
      className="d-flex align-items-center"
    >
      <FaShoppingCart className="me-2" /> Upgrade Now
    </Button>
  </Modal.Footer>
</Modal>

{/* Edit Auditor Modal */}
<Modal 
  show={showEditModal} 
  onHide={handleEditModalClose} 
  size="lg"
  centered
  backdrop="static"
>
  <Modal.Header closeButton className="bg-warning text-white">
    <Modal.Title>Edit Individual Auditor</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <Form onSubmit={handleEditSubmit} noValidate>
      {error && <Alert variant="danger">{error}</Alert>}
      {/* Personal Information Section */}
      <h5 className="border-bottom pb-2 mb-3">Personal Information</h5>

      {/* First Name and Last Name */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>First Name</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaUser /></InputGroup.Text>
              <Form.Control
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                isInvalid={!!errors.firstName}
                placeholder="Enter first name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Last Name</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaUser /></InputGroup.Text>
              <Form.Control
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                isInvalid={!!errors.lastName}
                placeholder="Enter last name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      {/* Contact Information Section */}
      <h5 className="border-bottom pb-2 mb-3 mt-4">Contact Information</h5>

      {/* Phone Number */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Phone Number</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaPhone /></InputGroup.Text>
              <Form.Control
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                isInvalid={!!errors.phoneNumber}
                placeholder="Enter phone number"
              />
              <Form.Control.Feedback type="invalid">
                {errors.phoneNumber}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      {/* Country */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Country</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaGlobe /></InputGroup.Text>
              <Form.Select
                name="country"
                value={formData.country}
                onChange={handleChange}
                isInvalid={!!errors.country}
              >
                {countries.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.country}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      {/* Security Section */}
      <h5 className="border-bottom pb-2 mb-3 mt-4">Security</h5>

      {/* Password Change Button */}
      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <div className="d-flex align-items-center mb-2">
              <Button
                variant="outline-primary"
                onClick={() => setShowPasswordFields(!showPasswordFields)}
                className="d-flex align-items-center"
              >
                <FaLock className="me-2" />
                {showPasswordFields ? 'Cancel Password Change' : 'Change Password'}
              </Button>
            </div>
            
            {showPasswordFields && (
              <Row className="mt-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>New Password</Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaLock /></InputGroup.Text>
                      <Form.Control
                        type={showEditPassword ? "text" : "password"} 
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        isInvalid={!!errors.password}
                        placeholder="Enter new password"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowEditPassword(!showEditPassword)}
                      >
                        {showEditPassword ? <FaEyeSlash /> : <FaEye />}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Confirm New Password</Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaLock /></InputGroup.Text>
                      <Form.Control
                        type={showEditConfirmPassword ? "text" : "password"} 
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        isInvalid={!!errors.confirmPassword}
                        placeholder="Confirm new password"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowEditConfirmPassword(!showEditConfirmPassword)}
                      >
                        {showEditConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {errors.confirmPassword}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Form.Group>
        </Col>
      </Row>
    </Form>
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={handleEditModalClose}>
      Cancel
    </Button>
    <Button 
      variant="warning" 
      onClick={handleEditSubmit}
      disabled={editLoading}
    >
      {editLoading ? "Updating..." : "Update Auditor"}
    </Button>
  </Modal.Footer>
</Modal>

    </Container>
  );
}

export default BusinessAuditorCreate;